/**
 * useUnitsPreference — distance unit preference (km / mi) with localStorage
 * persistence. Phase 2 v149 — first consumer of SettingPill primitive.
 *
 * Storage key follows the project convention `storica_*` (see `storica_theme`,
 * `storica_wellness_trend_range`). Silent fallback to default 'km' when
 * localStorage is unavailable (private mode, quota, server-side render).
 *
 * Default: 'km' (EU is primary launch market; US athletes opt-in to 'mi').
 */
import { useCallback, useEffect, useState } from "react";

export type DistanceUnit = "km" | "mi";

const STORAGE_KEY = "storica_distance_unit";
const DEFAULT_UNIT: DistanceUnit = "km";

function isUnit(value: unknown): value is DistanceUnit {
  return value === "km" || value === "mi";
}

function read(): DistanceUnit {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isUnit(raw) ? raw : DEFAULT_UNIT;
  } catch {
    // silent: localStorage unavailable
    return DEFAULT_UNIT;
  }
}

function write(next: DistanceUnit): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // silent: localStorage write failed — pref still applies this session
  }
}

/**
 * Hook returning current unit + setter that persists immediately and notifies
 * sibling components via the `storage` event (browser fires this only across
 * tabs by default — same-tab consumers re-read on render).
 */
export function useUnitsPreference(): {
  unit: DistanceUnit;
  setUnit: (next: DistanceUnit) => void;
} {
  const [unit, setUnitState] = useState<DistanceUnit>(() => read());

  // Cross-tab sync — if user changes pref in another tab, reflect it here.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && isUnit(e.newValue)) {
        setUnitState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setUnit = useCallback((next: DistanceUnit) => {
    write(next);
    setUnitState(next);
  }, []);

  return { unit, setUnit };
}

/** Миль в километре. */
export const MI_PER_KM = 0.6213711922;

/**
 * toDisplayDistance — километры в выбранную единицу, ЧИСЛОМ.
 *
 * ЗАЧЕМ ОТДЕЛЬНО ОТ `formatDistance`. Пересчёт нужен не только там, где число
 * печатают: график рисует столбики, и им нужна величина, а не строка. Пока
 * пересчёт жил внутри форматирования, график его не получал — карточка
 * «Объём» рисовала столбики в километрах и подписывала тултип «км», а итог
 * под тем же графиком пересчитывала в мили. Итог не сходился с суммой
 * столбиков, и оба числа выглядели одинаково правдоподобно.
 */
export function toDisplayDistance(km: number, unit: DistanceUnit): number {
  return unit === "mi" ? km * MI_PER_KM : km;
}

/**
 * formatDistance — convert km value to chosen unit + format with given
 * precision. Returns just the numeric string; caller appends the unit
 * label (so RTL/locale can format independently).
 */
export function formatDistance(
  km: number,
  unit: DistanceUnit,
  decimals = 2,
): string {
  return toDisplayDistance(km, unit).toFixed(decimals);
}

/**
 * unitLabel — i18n-friendly hook caller pass i18n.t. Returns the unit
 * abbreviation in user's preferred form.
 */
export function unitLabel(unit: DistanceUnit): string {
  return unit === "mi" ? "mi" : "km";
}
